import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { BarcodeScanner, BarcodeFormat } from '@capacitor-mlkit/barcode-scanning';
import { toast } from 'sonner';

export { BarcodeFormat };

const DEFAULT_FORMATS: BarcodeFormat[] = [
  BarcodeFormat.Ean13,
  BarcodeFormat.Ean8,
  BarcodeFormat.Code128,
  BarcodeFormat.QrCode,
  BarcodeFormat.Itf,
];

export function useBarcodeScanner() {
  const [isScanning, setIsScanning] = useState(false);
  const isAvailable = Capacitor.isNativePlatform();

  const scan = useCallback(
    async (formats: BarcodeFormat[] = DEFAULT_FORMATS): Promise<string | null> => {
      if (!isAvailable) {
        const manual = window.prompt(
          'Escanear código de barras (fallback web) — digite o código:'
        );
        return manual?.trim() || null;
      }

      setIsScanning(true);
      try {
        if (Capacitor.getPlatform() === 'android') {
          const { available } = await BarcodeScanner.isGoogleBarcodeScannerModuleAvailable();
          if (!available) {
            try {
              await BarcodeScanner.installGoogleBarcodeScannerModule();
              toast.info('Instalando módulo de scanner. Tente novamente em instantes.');
              return null;
            } catch (installErr) {
              console.error('[useBarcodeScanner] install module failed:', installErr);
              toast.error('Não foi possível instalar o módulo de scanner.');
              return null;
            }
          }
        }

        const { barcodes } = await BarcodeScanner.scan({ formats });
        const first = barcodes?.[0];
        if (!first) return null;
        return first.rawValue || first.displayValue || null;
      } catch (error: unknown) {
        const msg = error instanceof Error ? error.message : String(error);
        if (/cancel/i.test(msg)) return null;
        if (/permission|denied/i.test(msg)) {
          toast.warning('Permissão de câmera necessária para escanear');
        } else {
          console.error('[useBarcodeScanner] scan error:', error);
          toast.error('Erro ao escanear código');
        }
        return null;
      } finally {
        setIsScanning(false);
      }
    },
    [isAvailable]
  );

  return { scan, isAvailable, isScanning };
}
